import { BRAND_CONFIGS, isOnHlxHost } from '~/config/branding';
import { isPublicSitePath } from './publicSiteRoutes';

/** The satellite-related ClerkProvider options, spread into the provider config in main.tsx. */
type ClerkSatelliteOptions = {
  isSatellite?: true;
  domain?: string;
  satelliteAutoSync?: boolean;
};

/**
 * [MCV-1719] Clerk satellite options for the current page load.
 *
 * Off the HLX host this returns {}: MCV is the Clerk primary, not a satellite.
 * On HLX every page is a satellite, and `satelliteAutoSync` is switched off on
 * the public site pages (/, /faq, /about, /loginOrCreateAccount, ...).
 *
 * Why public pages must not auto-sync. With `satelliteAutoSync: true`, clerk-js
 * (`#ts`) sends a signed-out satellite visitor to the primary's sign-in page to
 * look for a session, unless the URL carries `__clerk_synced=true`. The brand
 * switch from MCV adds exactly that marker, but it only works if clerk-js can read
 * it. On pk_test (dev and staging) clerk-js is loaded from *.clerk.accounts.dev,
 * a third-party script, and Safari's Advanced Tracking and Fingerprinting
 * Protection then hides the query string from it after a cross-site click. clerk-js
 * sees no marker, hops to the primary, cannot read the return parameters there
 * either, and strands the visitor on the MCV sign-in card: "Switch to HealthcareLX"
 * flashes HLX and lands back on MCV. Public marketing, legal and role-choice pages
 * need no synced session, so they skip the hop. (pk_live serves clerk-js first
 * party, but a signed-out visitor still pays the per-load /v1/client/sync redirect
 * for nothing.)
 *
 * What still syncs.
 * - An explicit `__clerk_synced=false` (the signed-in switch, the satellite auth
 *   return) is honoured: clerk-js 6.x checks it before `satelliteAutoSync`.
 * - Auth, invite, registration and app routes (/auth/complete, /sign-in, /hlx/...)
 *   are not public site paths, so they keep autosync.
 *
 * `satelliteAutoSync` is read once, when clerk-js loads on a full page load, so
 * the pathname the page was loaded on decides it for that session.
 */
export const buildClerkSatelliteOptions = ({
  hostname,
  pathname,
  publishableKey,
}: {
  hostname: string;
  pathname: string;
  publishableKey: string;
}): ClerkSatelliteOptions =>
  isOnHlxHost(hostname)
    ? {
        isSatellite: true,
        // pk_live_* uses the canonical apex; pk_test_* uses the current hostname.
        domain: publishableKey.startsWith('pk_live_') ? BRAND_CONFIGS.hlx.domain : hostname,
        satelliteAutoSync: !isPublicSitePath(pathname),
      }
    : {};
